import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import API from '../lib/api';

export default function AuthCallback() {
  const hasProcessed = useRef(false);
  const navigate = useNavigate();
  const { login } = useAuth();

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      // Check for session_id in both hash and search params
      const hash = window.location.hash;
      const search = window.location.search;
      let sessionId = null;
      
      console.log('[AuthCallback] Processing auth...', { 
        hash: hash?.substring(0, 50), 
        search: search?.substring(0, 50), 
        pathname: window.location.pathname,
        href: window.location.href?.substring(0, 100)
      });
      
      // Try hash first (#session_id=xxx)
      if (hash && hash.includes('session_id=')) {
        sessionId = hash.split('session_id=')[1]?.split('&')[0];
        console.log('[AuthCallback] Found session_id in hash');
      } 
      // Then try query params (?session_id=xxx)
      else if (search && search.includes('session_id=')) {
        const params = new URLSearchParams(search);
        sessionId = params.get('session_id');
        console.log('[AuthCallback] Found session_id in query params');
      }
      
      if (!sessionId) { 
        console.error('[AuthCallback] No session_id found in URL');
        navigate('/login', { replace: true }); 
        return; 
      }

      console.log('[AuthCallback] Session ID found:', sessionId.substring(0, 8) + '...');

      try {
        console.log('[AuthCallback] Calling /api/auth/session...');
        const res = await API.get(`/auth/session?session_id=${sessionId}`);
        console.log('[AuthCallback] Session response:', { 
          hasToken: !!res.data.token, 
          hasUser: !!res.data.user,
          userEmail: res.data.user?.email,
          userId: res.data.user?.user_id
        });
        
        if (res.data.token && res.data.user) {
          // Call login which sets both user state and loading=false
          console.log('[AuthCallback] Calling login function...');
          login(res.data.token, res.data.user);
          
          // Verify token was stored
          const storedToken = localStorage.getItem('auth_token');
          console.log('[AuthCallback] Token stored in localStorage:', !!storedToken);
          
          console.log('[AuthCallback] Login successful, redirecting to dashboard');
          
          // Use setTimeout to ensure React state has fully propagated
          // This prevents the race condition with ProtectedRoute
          setTimeout(() => {
            window.history.replaceState(null, '', '/dashboard');
            navigate('/dashboard', { replace: true });
          }, 150);
        } else {
          console.error('[AuthCallback] Invalid response - missing token or user', res.data);
          navigate('/login', { replace: true });
        }
      } catch (err) {
        console.error('[AuthCallback] Auth callback error:', {
          status: err.response?.status,
          data: err.response?.data,
          message: err.message
        });
        navigate('/login', { replace: true });
      }
    };
    processAuth();
  }, [navigate, login]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-center space-y-4">
        <div className="w-10 h-10 border-2 border-gold border-t-transparent rounded-full animate-spin mx-auto" />
        <p className="text-sm text-muted-foreground">Authenticating...</p>
      </div>
    </div>
  );
}
